import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import CreateProjectModal from '../components/CreateProjectModal';
import { api } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [account, setAccount]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const { t, formatDate } = useI18n();

  useEffect(() => {
    Promise.all([
      api.listProjects().then(r => r.ok ? r.json() : []),
      api.getAccount().then(r => r.ok ? r.json() : null),
    ]).then(([projs, acc]) => {
      setProjects(projs || []);
      setAccount(acc);
      setLoading(false);
    });
  }, []);

  // Block project creation until the e-mail is confirmed (PIN-254).
  const unverified = account && !account.EmailVerified;

  const handleDelete = async (id) => {
    if (!window.confirm(t('projects.deleteConfirm'))) return;
    const res = await api.deleteProject(id);
    if (res.ok || res.status === 204) {
      setProjects(ps => ps.filter(p => p.ID !== id));
    }
  };

  const header = (
    <>
      <h1 className="uh-title">{t('projects.title')}</h1>
      <div className="uh-actions">
        <button className="uh-btn uh-btn-primary" onClick={() => setShowCreate(true)} disabled={unverified} title={unverified ? t('verifyBanner.text') : undefined}>{t('projects.newProject')}</button>
      </div>
    </>
  );

  return (
    <Layout sidebar={<Sidebar />} header={header}>
      <p className="uh-muted" style={{ marginTop: 0, marginBottom: 16, fontSize: '0.9em' }}>{t('projects.intro')}</p>
      {loading ? (
        <p className="uh-empty">{t('common.loading')}</p>
      ) : projects.length === 0 ? (
        <p className="uh-empty">{t('projects.noProjectsYet')}</p>
      ) : (
        <table className="uh-table">
          <thead>
            <tr>
              <th>{t('projects.name')}</th>
              <th>{t('projects.status')}</th>
              <th>{t('projects.checks')}</th>
              <th>{t('projects.created')}</th>
              <th>{t('projects.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <tr key={p.ID}>
                <td data-label={t('projects.name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span
                      className={`uh-status-dot uh-status-dot--${p.Enabled ? 'ok' : 'muted'}`}
                      aria-hidden="true"
                    />
                    <Link to={`/projects/${p.ID}`} className="uh-link">{p.ProjectName}</Link>
                  </span>
                </td>
                <td data-label={t('projects.status')}>
                  <span className={`uh-badge ${p.Enabled ? 'ok' : 'muted'}`}>
                    {p.Enabled ? t('common.active') : t('common.paused')}
                  </span>
                </td>
                <td data-label={t('projects.checks')}>{p.Checks?.length || 0}</td>
                <td data-label={t('projects.created')} style={{ fontSize: 12, color: 'var(--subtext)' }}>
                  {formatDate(p.CreatedAt, { dateStyle: 'medium' })}
                </td>
                <td className="uh-td-actions">
                  <div className="uh-actions">
                    <Link to={`/projects/${p.ID}`} className="uh-btn uh-btn-sm">{t('common.open')}</Link>
                    <button className="uh-btn uh-btn-sm uh-btn-danger" onClick={() => handleDelete(p.ID)}>{t('common.delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={p => setProjects(ps => [p, ...ps])}
        />
      )}
    </Layout>
  );
};

export default ProjectsPage;
